import React, { useState, useEffect, useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaGithub, FaBolt, FaPlus, FaHome, FaDownload, FaLightbulb, FaSignOutAlt, FaComments, FaRedo, FaFolder, FaTerminal, FaSearch, FaTimes, FaCrown } from "react-icons/fa";
import Logo from "../components/Logo";
import api from "../api";
import { getApiUrl } from "../utils/apiConfig";
import "../styles/ChatPrompt.css";
import ChatHistory from "../components/ChatHistory";
import ChatUI from "../components/ChatUI";
import ProfileSelector from "../components/ProfileSelector";
import WorkingDirectoryDropdown from "../components/WorkingDirectoryDropdown";
import NewChatModal from "../components/NewChatModal";
import FileBrowser from "../components/FileBrowser";
import Terminal from "../components/Terminal";
import { filterSuggestions, getRandomSuggestions, getCategories } from "../data/suggestions";

const baseURL = `${getApiUrl()}/api`;

function ChatPrompt() {
  const location = useLocation();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [selectedProfileUuid, setSelectedProfileUuid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const messagesEndRef = useRef(null);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  
  // Suggestions state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // New Chat Modal state
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const newChatButtonRef = useRef(null);
  
  // File Browser state
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  
  // Terminal state
  const [showTerminal, setShowTerminal] = useState(false);

  useEffect(() => {
    fetchUserInfo();
    // Ensure sidebar starts at the top position
    const chatHistoryContainer = document.querySelector('.chat-history-container');
    if (chatHistoryContainer) {
      chatHistoryContainer.scrollTop = 0;
    }
    const chatList = document.querySelector('.chat-list');
    if (chatList) {
      chatList.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (selectedProfileUuid) {
      fetchChats();
      setError(""); // Clear error when profile selected
      // Reset scroll position when profile changes
      setTimeout(() => {
        const chatHistoryContainer = document.querySelector('.chat-history-container');
        if (chatHistoryContainer) {
          chatHistoryContainer.scrollTop = 0;
        }
        const chatList = document.querySelector('.chat-list');
        if (chatList) {
          chatList.scrollTop = 0;
        }
      }, 100);
    }
  }, [selectedProfileUuid]);

  useEffect(() => {
    if (selectedChatId) {
      fetchMessages(selectedChatId);
    } else {
      setMessages([]);
    }
  }, [selectedChatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle suggested prompts from navigation
  useEffect(() => {
    if (location.state?.suggestedPrompt) {
      setInputMessage(location.state.suggestedPrompt);
      // Clear the state to prevent refilling on re-renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Update suggestions when search query changes
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSuggestions(getRandomSuggestions(5));
    } else {
      const filtered = filterSuggestions(searchQuery);
      setSuggestions(filtered.slice(0, 10)); // Show up to 10 filtered results
    }
  }, [searchQuery]);

  // Focus search input when suggestions popup opens
  useEffect(() => {
    if (showSuggestions && searchInputRef.current) {
      // Small delay to ensure the popup is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else if (!showSuggestions) {
      // Clear search when popup closes
      setSearchQuery("");
    }
  }, [showSuggestions]);

  // Suggestions functionality - removed defaultSuggestions array and getRandomSuggestions function
  // Now using functions from suggestions.js

  // Load suggestions on component mount
  useEffect(() => {
    setSuggestions(getRandomSuggestions(5));
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };

  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    setShowSuggestions(false);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleProfileChange = (profileUuid) => {
    setSelectedProfileUuid(profileUuid);
    setSelectedChatId(null);
    setMessages([]);
    setChats([]); // Clear chats when profile changes
  };

  const fetchChats = async (skipAutoSelect = false) => {
    if (!selectedProfileUuid) return;
    
    try {
      const response = await api.get(`${baseURL}/profiles/${selectedProfileUuid}/chats/`);
      
      // Ensure each chat has a name property
      const chatsWithNames = response.data.map(chat => ({
        ...chat,
        name: chat.name || `Chat - ${new Date(chat.created_at).toLocaleString()}`
      }));
      
      setChats(chatsWithNames);
      
      // If there are chats but none is selected, select the first one
      // Skip this if skipAutoSelect is true (when called after creating a new chat)
      if (chatsWithNames.length > 0 && !selectedChatId && !skipAutoSelect) {
        setSelectedChatId(chatsWithNames[0].id);
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  const fetchMessages = async (chatId) => {
    if (!chatId || !selectedProfileUuid) return;
    
    try {
      const response = await api.get(`${baseURL}/profiles/${selectedProfileUuid}/chats/${chatId}/`);
      if (Array.isArray(response.data)) {
        // Preserve any placeholder messages that are currently being processed
        setMessages(prevMessages => {
          const placeholderMessages = prevMessages.filter(msg => msg.isPlaceholder === true);
          const serverMessages = response.data.map(msg => ({
            ...msg,
            id: msg.id || `server-${msg.content.substring(0, 20)}-${Date.now()}`,
            isTemporary: false,
            isPlaceholder: false
          }));
          
          // Start with server messages
          let combinedMessages = [...serverMessages];
          
          // Only keep placeholder messages that don't have corresponding server responses
          placeholderMessages.forEach(placeholderMsg => {
            // For placeholder messages, check if we got a corresponding real response
            const hasCorrespondingResponse = serverMessages.some(serverMsg => {
              // Look for recent assistant messages that could be the response to this placeholder
              if (serverMsg.role === 'assistant' && placeholderMsg.role === 'assistant') {
                const serverTime = new Date(serverMsg.timestamp || serverMsg.created_at).getTime();
                const now = Date.now();
                // Consider it corresponding if it's a recent assistant message (within last 5 minutes)
                return (now - serverTime) < 300000; // 5 minutes
              }
              return false;
            });
            
            // Only keep the placeholder if no corresponding response was found
            if (!hasCorrespondingResponse) {
              combinedMessages.push(placeholderMsg);
            }
          });
          
          // Sort by timestamp if available, with fallback to ensure consistent ordering
          return combinedMessages.sort((a, b) => {
            // First, try to sort by timestamp
            if (a.timestamp && b.timestamp) {
              return new Date(a.timestamp) - new Date(b.timestamp);
            }
            if (a.created_at && b.created_at) {
              return new Date(a.created_at) - new Date(b.created_at);
            }
            
            // Fallback: ensure user messages come before assistant messages for same time
            if (a.role !== b.role) {
              return a.role === 'user' ? -1 : 1;
            }
            
            // Final fallback: maintain existing order
            return 0;
          });
        });
      } else {
        console.error("Expected array of messages but got:", response.data);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !selectedProfileUuid) return;
    
    // Generate a unique temporary ID for this message pair
    const tempId = Date.now() + Math.random();
    const userMessage = {
      id: `user-${tempId}`,
      content: inputMessage,
      role: "user",
      isTemporary: true
    };
    
    // Create a placeholder assistant message immediately
    const placeholderAssistantMessage = {
      id: `assistant-${tempId}`,
      content: "🤔 Analysing...",
      role: "assistant",
      isTemporary: true,
      isPlaceholder: true
    };
    
    // Add both user message and placeholder assistant message to UI immediately
    setMessages(prevMessages => [...prevMessages, userMessage, placeholderAssistantMessage]);
    
    // Store the input message for the API call
    const messageToSend = inputMessage;
    setInputMessage("");

    // Track the request timestamp for polling
    const requestStartTime = Date.now();
    
    // Update placeholder message after 10 seconds to indicate complex processing
    const longRunningTimeout = setTimeout(() => {
      setMessages(prevMessages => {
        return prevMessages.map(msg => {
          if (msg.id === `assistant-${tempId}` && msg.isPlaceholder) {
            return {
              ...msg,
              content: "⏳ Processing complex query... This may take a few moments for multi-step operations."
            };
          }
          return msg;
        });
      });
    }, 10000);

    try {
      // Store the current chat ID to use throughout this function
      let currentChatId = selectedChatId;
      
      // Use the profile-specific endpoint
      const endpoint = currentChatId 
        ? `${baseURL}/profiles/${selectedProfileUuid}/chats/${currentChatId}/`
        : `${baseURL}/profiles/${selectedProfileUuid}/chats/`;
        
      const response = await api.post(endpoint, {
        message: messageToSend,
      });

      if (!currentChatId) {
        // If this was a new chat, get the new chat ID
        if (response.data && response.data.id) {
          currentChatId = response.data.id;
          setSelectedChatId(currentChatId);
          // Update the chats list without auto-selecting
          await fetchChats(true);
        } else {
          console.error("New chat created but no ID returned:", response.data);
        }
      }
      
      // Clear the long-running timeout since we got a response
      clearTimeout(longRunningTimeout);
      
      // Update the specific placeholder message with the actual response
      setMessages(prevMessages => {
        return prevMessages.map(msg => {
          if (msg.id === `assistant-${tempId}` && msg.isPlaceholder) {
            return {
              id: `assistant-${tempId}`,
              content: response.data.message || "No response received",
              role: "assistant",
              isTemporary: false,
              isPlaceholder: false
            };
          }
          // Also update the user message to be non-temporary
          if (msg.id === `user-${tempId}`) {
            return {
              ...msg,
              isTemporary: false
            };
          }
          return msg;
        });
      });
      
    } catch (error) {
      console.error("Error sending message:", error);
      
      // Clear the long-running timeout since we got an error
      clearTimeout(longRunningTimeout);
      
      // Check if this might be a timeout error where the backend is still processing
      const isLikelyTimeout = error.code === 'ECONNABORTED' || 
                             error.message?.includes('timeout') ||
                             error.response?.status === 504 ||
                             error.response?.status === 502;
      
      if (isLikelyTimeout && selectedChatId) {
        console.log("Potential timeout detected, starting response polling...");
        
        // Update placeholder to show we're waiting for a delayed response
        setMessages(prevMessages => {
          return prevMessages.map(msg => {
            if (msg.id === `assistant-${tempId}` && msg.isPlaceholder) {
              return {
                ...msg,
                content: "⏳ Processing is taking longer than expected. Please wait while we check for your response..."
              };
            }
            return msg;
          });
        });
        
        // Start polling for the response
        const pollForResponse = async (attempt = 1) => {
          const maxAttempts = 20; // Poll for up to 10 minutes (30s * 20 = 600s)
          const pollInterval = 30000; // 30 seconds between polls
          
          if (attempt > maxAttempts) {
            // Give up after max attempts
            setMessages(prevMessages => {
              return prevMessages.map(msg => {
                if (msg.id === `assistant-${tempId}` && msg.isPlaceholder) {
                  return {
                    id: `assistant-${tempId}`,
                    content: "⚠️ The request is taking longer than expected. The response may appear shortly. You can also try refreshing the chat.",
                    role: "assistant",
                    isTemporary: false,
                    isError: true,
                    isPlaceholder: false
                  };
                }
                return msg;
              });
            });
            return;
          }
          
          setTimeout(async () => {
            try {
              // Fetch latest messages to check if response arrived
              const response = await api.get(`${baseURL}/profiles/${selectedProfileUuid}/chats/${selectedChatId}/`);
              
              if (Array.isArray(response.data)) {
                // Look for new assistant messages created after the request started
                const newAssistantMessages = response.data.filter(msg => 
                  msg.role === 'assistant' && 
                  new Date(msg.timestamp || msg.created_at).getTime() > requestStartTime
                );
                
                if (newAssistantMessages.length > 0) {
                  // Found a response! Update the UI
                  const latestResponse = newAssistantMessages[newAssistantMessages.length - 1];
                  
                  setMessages(prevMessages => {
                    return prevMessages.map(msg => {
                      if (msg.id === `assistant-${tempId}` && msg.isPlaceholder) {
                        return {
                          id: `assistant-${tempId}`,
                          content: latestResponse.content,
                          role: "assistant",
                          isTemporary: false,
                          isPlaceholder: false
                        };
                      }
                      // Also update the user message to be non-temporary
                      if (msg.id === `user-${tempId}`) {
                        return {
                          ...msg,
                          isTemporary: false
                        };
                      }
                      return msg;
                    });
                  });
                  
                  console.log(`Response found after ${attempt} polling attempts`);
                  return; // Success, stop polling
                }
              }
              
              // No response yet, continue polling
              console.log(`Polling attempt ${attempt}: No response yet, continuing...`);
              pollForResponse(attempt + 1);
              
            } catch (pollError) {
              console.error("Error during response polling:", pollError);
              // Continue polling despite errors
              pollForResponse(attempt + 1);
            }
          }, pollInterval);
        };
        
        // Start polling after a short delay
        setTimeout(() => pollForResponse(), 5000);
        
      } else {
        // Handle other types of errors normally
        // Extract error message from backend response if available
        let errorMessage = "⚠️ An error occurred while sending the message. Please try again.";
        
        if (error.response) {
          const status = error.response.status;
          const data = error.response.data;
          
          if (status === 403 && data?.error) {
            // Profile disabled or subscription issue
            errorMessage = `🔒 ${data.error}`;
          } else if (status === 400 && data?.error) {
            // Bad request with specific error message
            errorMessage = `⚠️ ${data.error}`;
          } else if (status === 429) {
            // Rate limited
            errorMessage = "⚠️ You've reached your daily credit limit. Please upgrade your plan for more credits.";
          } else if (data?.error) {
            // Other errors with message
            errorMessage = `⚠️ ${data.error}`;
          } else if (data?.detail) {
            // DRF style errors
            errorMessage = `⚠️ ${data.detail}`;
          }
        }
        
        setMessages(prevMessages => {
          return prevMessages.map(msg => {
            if (msg.id === `assistant-${tempId}` && msg.isPlaceholder) {
              return {
                id: `assistant-${tempId}`,
                content: errorMessage,
                role: "assistant",
                isTemporary: false,
                isError: true,
                isPlaceholder: false
              };
            }
            // Also update the user message to be non-temporary
            if (msg.id === `user-${tempId}`) {
              return {
                ...msg,
                isTemporary: false
              };
            }
            return msg;
          });
        });
      }
    }
  };

  const createNewChat = () => {
    if (!selectedProfileUuid) {
      setError("Please select a profile before creating a new chat");
      return;
    }
    
    setShowNewChatModal(true);
  };

  const handleCreateChatConfirm = async (chatName) => {
    setShowNewChatModal(false);
    
    try {
      const response = await api.post(`${baseURL}/chat/create/`, {
        profile_id: selectedProfileUuid,
        name: chatName.trim() || "New Chat" // Use provided name or default
      });
      
      const newChat = response.data;
      if (newChat && newChat.chat_id) {
        // Add the new chat to the list
        const chatItem = {
          id: newChat.chat_id,
          profile: selectedProfileUuid,
          name: newChat.name,
          created_at: newChat.created_at
        };
        setChats(prevChats => [chatItem, ...prevChats]);
        setSelectedChatId(newChat.chat_id);
        setMessages([]);
        setError(""); // Clear any errors on success
      } else {
        console.error("Created chat but received unexpected response:", newChat);
        setError("Something went wrong when creating a new chat.");
      }
    } catch (error) {
      console.error("Error creating a new chat:", error);
      setError("Failed to create new chat. Please try again.");
    }
  };

  const handleCreateChatCancel = () => {
    setShowNewChatModal(false);
  };

  const fetchUserInfo = async () => {
    try {
      const response = await api.get(`${baseURL}/user/profile/`);
      setUserInfo(response.data);
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const handleReportResponse = async (reportData) => {
    try {
      const response = await api.post(`${baseURL}/report-response/`, reportData);
      if (response.data.success) {
        // Show success message (you can implement a notification system here)
        console.log("Response reported successfully:", response.data.message);
      }
    } catch (error) {
      console.error("Error reporting response:", error);
      // Show error message (you can implement a notification system here)
    }
  };

  function formatMessageContent(content) {
    // Since backend sends HTML content, just return it as-is
    // Only do minimal processing for basic text formatting if needed
    if (content.includes('<') && content.includes('>')) {
      // Content already contains HTML, return as-is
      return content;
    } else {
      // Plain text content, convert newlines to <br>
      return content.replace(/\n/g, "<br>");
    }
  }

  return (
    <div className="App">
      {/* Top Navigation Bar */}
      <nav className="top-navbar">
        <div className="navbar-content">
          <div className="navbar-left">
            <Logo size="small" showText={false} linkTo="/chat" />
            <div className="nav-links">
              <button 
                className="nav-link active"
                onClick={() => navigate('/chat')}
              >
                <FaComments /> Chat
              </button>
              <button 
                className="nav-link"
                onClick={() => navigate('/downloads')}
              >
                <FaDownload /> Downloads
              </button>
              <button 
                className="nav-link subscription-link"
                onClick={() => navigate('/subscription')}
              >
                <FaCrown /> Subscription
              </button>
              <WorkingDirectoryDropdown selectedProfileUuid={selectedProfileUuid} />
              <div className="nav-suggestions" ref={dropdownRef}>
                <button 
                  className="nav-link"
                  onClick={() => {
                    if (!showSuggestions) {
                      setSuggestions(getRandomSuggestions(5));
                    }
                    setShowSuggestions(!showSuggestions);
                  }}
                >
                  <FaLightbulb /> Suggestions
                </button>
                
                {showSuggestions && (
                  <div className="suggestions-dropdown">
                    <div className="suggestions-header">
                      <span>Query Suggestions</span>
                      <button 
                        className="suggestions-reload-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchQuery("");
                          setSuggestions(getRandomSuggestions(5));
                        }}
                        title="Refresh suggestions"
                      >
                        <FaRedo />
                      </button>
                    </div>
                    
                    {/* Search Bar */}
                    <div className="suggestions-search">
                      <div className="search-input-container">
                        <FaSearch className="search-icon" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          className="search-input"
                          placeholder="Search suggestions (e.g., 'virustotal', 'pe', 'network')"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {searchQuery && (
                          <button
                            className="clear-search-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchQuery("");
                              searchInputRef.current?.focus();
                            }}
                            title="Clear search"
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="suggestions-list">
                      {suggestions.length > 0 ? (
                        suggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => handleSuggestionClick(typeof suggestion === 'string' ? suggestion : suggestion.text)}
                            className="suggestion-item"
                          >
                            {typeof suggestion === 'string' ? suggestion : suggestion.text}
                            {typeof suggestion === 'object' && suggestion.category && (
                              <span className="suggestion-category">{suggestion.category}</span>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="no-suggestions">
                          No suggestions found for "{searchQuery}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="navbar-right">
            <button 
              className={`nav-link file-browser-icon ${showFileBrowser ? 'active' : ''}`}
              onClick={() => setShowFileBrowser(!showFileBrowser)}
              title="Toggle File Browser"
            >
              <FaFolder /> Files
            </button>
            <button 
              className={`nav-link terminal-icon ${showTerminal ? 'active' : ''}`}
              onClick={() => setShowTerminal(!showTerminal)}
              title="Toggle Console"
            >
              <FaTerminal /> Console
            </button>
            <div className="profile-selector-wrapper">
              <ProfileSelector 
                selectedProfileId={selectedProfileUuid}
                setSelectedProfileId={setSelectedProfileUuid}
                onProfileChange={handleProfileChange}
              />
            </div>
            <button 
              className="logout-btn"
              onClick={handleLogout}
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </nav>
      <div className="chat-container">
        <div className="chat-history-container">
          <button 
            ref={newChatButtonRef}
            className="new-chat-button" 
            onClick={createNewChat}
          >
            <FaPlus />
            <span>New Chat</span>
          </button>
          {error && <div className="error-message">{error}</div>}
          <ChatHistory
            chats={chats}
            selectedChatId={selectedChatId}
            setSelectedChatId={setSelectedChatId}
          />
        </div>
        <ChatUI
          messages={messages}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          sendMessage={sendMessage}
          formatMessageContent={formatMessageContent}
          messagesEndRef={messagesEndRef}
          onReportResponse={handleReportResponse}
          selectedProfileUuid={selectedProfileUuid}
          userInfo={userInfo}
        />
      </div>
      
      <NewChatModal
        isOpen={showNewChatModal}
        onClose={handleCreateChatCancel}
        onConfirm={handleCreateChatConfirm}
        buttonRef={newChatButtonRef}
      />
      
      <FileBrowser
        profileUuid={selectedProfileUuid}
        isOpen={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
      />
      
      <Terminal
        profileUuid={selectedProfileUuid}
        isOpen={showTerminal}
        onClose={() => setShowTerminal(false)}
      />
    </div>
  );
}

export default ChatPrompt;